import React, { useState } from 'react';
import { Modal, Button, Table, Alert, Spinner, Badge, Form } from 'react-bootstrap';
import {
  useAdminSystemTableColumnOptions,
  useCreateAdminSystemTableColumnOption,
  useUpdateAdminSystemTableColumnOption,
  useDeleteAdminSystemTableColumnOption
} from 'api/hooks/use-admin-system-table-column-options';
import { useAdminMunicipalityOptions } from 'api/hooks/use-admin-municipalities';

const SystemTableColumnOptionForm = ({ systemTableColumnId, option, onSuccess, onCancel }) => {
  const { data: municipalities = [] } = useAdminMunicipalityOptions();
  const createMutation = useCreateAdminSystemTableColumnOption({
    systemTableColumnId,
    options: { onSuccess }
  });
  const updateMutation = useUpdateAdminSystemTableColumnOption({
    id: option?.id,
    options: { onSuccess }
  });

  const [formData, setFormData] = useState({
    system_table_column_id: systemTableColumnId,
    municipality_id: option?.municipality_id || '',
    value: option?.value || '',
    label: option?.label || '',
    order_position: option?.order_position || 0
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (submitData.municipality_id === '') {
      submitData.municipality_id = null;
    }

    if (option) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;
  const error = createMutation.error || updateMutation.error;

  return (
    <Form onSubmit={handleSubmit} className="border p-3 mb-3 bg-light">
      {error && <Alert variant="danger">{error.message}</Alert>}

      <Form.Group className="mb-2">
        <Form.Label>Municipality (Optional)</Form.Label>
        <Form.Select
          name="municipality_id"
          value={formData.municipality_id}
          onChange={handleChange}
          size="sm"
        >
          <option value="">Global (All Municipalities)</option>
          {municipalities?.map((muni) => (
            <option key={muni.id} value={muni.id}>
              {muni.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Value *</Form.Label>
        <Form.Control
          type="text"
          name="value"
          value={formData.value}
          onChange={handleChange}
          required
          size="sm"
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Label *</Form.Label>
        <Form.Control
          type="text"
          name="label"
          value={formData.label}
          onChange={handleChange}
          required
          size="sm"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Order Position *</Form.Label>
        <Form.Control
          type="number"
          name="order_position"
          value={formData.order_position}
          onChange={handleChange}
          required
          size="sm"
        />
      </Form.Group>

      <div>
        <Button type="submit" variant="primary" size="sm" disabled={isSubmitting} className="me-2">
          {isSubmitting ? 'Saving...' : (option ? 'Update' : 'Create')}
        </Button>
        <Button type="button" variant="light-primary" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

const SystemTableColumnOptionsModal = ({ show, onHide, systemTableColumn }) => {
  const { data: options, isLoading, error } = useAdminSystemTableColumnOptions({
    systemTableColumnId: systemTableColumn?.id
  });
  const deleteMutation = useDeleteAdminSystemTableColumnOption();
  const [showForm, setShowForm] = useState(false);
  const [editingOption, setEditingOption] = useState(null);

  const handleEdit = (option) => {
    setEditingOption(option);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingOption(null);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingOption(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingOption(null);
  };

  const handleDelete = (option) => {
    if (window.confirm('Are you sure you want to delete this option?')) {
      deleteMutation.mutate(option.id);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Manage Options - {systemTableColumn?.display_name} ({systemTableColumn?.system_name})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!showForm && (
          <div className="mb-3">
            <Button onClick={handleCreate} variant="primary" size="sm">
              Add Option
            </Button>
          </div>
        )}

        {showForm && (
          <SystemTableColumnOptionForm
            systemTableColumnId={systemTableColumn.id}
            option={editingOption}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        )}

        {isLoading ? (
          <div className="text-center p-3">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger">Error loading options: {error.message}</Alert>
        ) : options && options.length > 0 ? (
          <Table bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Municipality</th>
                <th>Value</th>
                <th>Label</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {options.map((option) => (
                <tr key={option.id}>
                  <td>
                    {option.municipality_name ? (
                      option.municipality_name
                    ) : (
                      <Badge bg="secondary">Global</Badge>
                    )}
                  </td>
                  <td><code>{option.value}</code></td>
                  <td>{option.label}</td>
                  <td>{option.order_position}</td>
                  <td>
                    <Button
                      onClick={() => handleEdit(option)}
                      variant="warning"
                      size="sm"
                      className="me-2"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(option)}
                      variant="danger"
                      size="sm"
                      disabled={deleteMutation.isLoading}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Alert variant="info">No options found. Add one to get started.</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light-primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SystemTableColumnOptionsModal;
